// @ts-nocheck
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, RefreshCw } from 'lucide-react';
import { RemotionPlayer } from './RemotionPlayer';
import { useQuery, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { Loader } from '@/components/ui/loader';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { LeftArrowIcon } from '@/components/ui/icons/left-arrow-icon';
import { RightArrowIcon } from '@/components/ui/icons/right-arrow-icon';
import { SmileIcon } from '@/components/ui/icons/smile-icon';
import { FrownIcon } from '@/components/ui/icons/frown-icon';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CreditBalance } from '@/components/shared/CreditBalance';

// Define a type for video scenes matching our updated schema
type VideoScene = {
	_id?: Id<'videoScenes'>;
	videoId: Id<'videos'>;
	sceneNumber: number;
	duration: number;
	imageUrl: string;
	text: string;
	transition?: string;
	captionText?: string;
	captionSegments?: Array<{
		word: string;
		start: number;
		end: number;
		confidence?: number;
	}>;
	audioDetails?: {
		url: string;
		startTime: number;
		endTime: number;
		sceneAudioUrl?: string;
	};
	externalId?: string;
	downloadUrl?: string;
	createdAt: string;
	updatedAt: string;
	status: string;
};

export const VideoTimelinePage: React.FC = () => {
	const { user } = useAuth();
	const { videoId } = useParams<{ videoId: string }>();
	const navigate = useNavigate();
	const [isRendering, setIsRendering] = useState(false);
	const [selectedSceneIndex] = useState(0);
	const [regeneratingSceneIds, setRegeneratingSceneIds] = useState<string[]>(
		[]
	);
	// Add state to track selected image index for each scene
	const [selectedImageIndices, setSelectedImageIndices] = useState<
		Record<string, number>
	>({});
	// Track animation direction for slide effect
	const [slideDirection, setSlideDirection] = useState<
		Record<string, 'left' | 'right'>
	>({});
	// Track animation state
	const [isAnimating, setIsAnimating] = useState<Record<string, boolean>>({});
	// Add a new state to handle fade transitions
	const [fadeState, setFadeState] = useState<
		Record<string, 'in' | 'out' | 'none'>
	>({});
	// Track hover state for arrows
	const [hoveredArrows, setHoveredArrows] = useState<
		Record<string, 'left' | 'right' | null>
	>({});
	// Track preloaded images
	const [preloadedImages, setPreloadedImages] = useState<
		Record<string, boolean>
	>({});
	const [clickedApproveSceneId, setClickedApproveSceneId] = useState<
		string | null
	>(null);
	const [clickedDisapproveSceneId, setClickedDisapproveSceneId] = useState<
		string | null
	>(null);
	// State for the regeneration confirmation dialog
	const [isRegenerateConfirmOpen, setIsRegenerateConfirmOpen] =
		useState(false);
	const [sceneToRegenerateId, setSceneToRegenerateId] = useState<
		string | null
	>(null);

	// Use Convex action to render video server-side
	const renderVideoAction = useAction(api.videos.renderVideo);

	// Server-side action is not available, will use direct API calls
	const updateSceneStatusAction: unknown = undefined;

	// Fetch video data from Convex (moved up)
	const videoData = useQuery(api.videos.getVideoById, {
		videoId: videoId as unknown as Id<'videos'>,
	});

	// Fetch video scenes from Convex (moved up)
	const videoScenes = useQuery(api.videos.getVideoScenes, {
		videoId: videoId as unknown as Id<'videos'>,
	});

	// Debug useEffect for data
	// useEffect(() => {
	// 	console.log('Video data:', videoData);
	// 	console.log('Video scenes:', videoScenes);
	// }, [videoData, videoScenes]);

	// Memoize formatted data for RemotionPlayer
	const formattedVideoData = useMemo(() => {
		if (!videoData) return null;

		// Extract image URLs from both sources
		const imageUrls = videoData.imageUrls || [];
		// When formatting for the main player, maybe we don't need scene images here?
		// Or we need a specific scene's image based on selectedSceneIndex
		// const sceneImages = videoScenes
		// 	? videoScenes.map((scene: any) => scene.imageUrl).filter(Boolean)
		// 	: [];

		// Format data for the player
		return {
			title: videoData.title,
			images: imageUrls, // Use main video image URLs for now
			audioUrl: videoData.audioUrl,
			captionJson: videoData.captionJson || [],
			width: videoData.width || 720,
			height: videoData.height || 1280,
		};
	}, [videoData]); // Removed videoScenes dependency for now

	// Function to update scene status - now defined after useQuery hooks
	const updateSceneStatus = useCallback(
		async (sceneId: Id<'videoScenes'>, status: string) => {
			console.log(`Setting scene ${sceneId} status to ${status}`);

			// Update local state for immediate UI feedback
			if (status === 'regenerating') {
				setRegeneratingSceneIds((prev) =>
					prev.includes(sceneId.toString())
						? prev
						: [...prev, sceneId.toString()]
				);
			} else {
				setRegeneratingSceneIds((prev) =>
					prev.filter((id) => id !== sceneId.toString())
				);
			}

			// Find the scene to get its externalId for the API call
			const scene = videoScenes?.find(
				(s: VideoScene) => s._id?.toString() === sceneId.toString()
			);
			const idToSend = scene?.externalId || sceneId.toString();

			console.log(`🔍 Found scene:`, scene ? 'Yes' : 'No');
			console.log(`🔑 Using ID for API call: ${idToSend}`);
			console.log(
				`🏷️ ID Type: ${scene?.externalId ? 'externalId' : 'convexId'}`
			);

			// Try the server-side approach first if available
			try {
				// Check if server-side action is available
				if (updateSceneStatusAction) {
					console.log(`🔄 Using server-side action to update status`);
					try {
						const result = await updateSceneStatusAction({
							sceneId: idToSend,
							status,
						});
						console.log(
							`✅ Server-side API call succeeded:`,
							result
						);
						return true;
					} catch (serverError) {
						console.error(
							`❌ Server-side action failed: ${serverError}`
						);
						console.log('⚠️ Falling back to direct API call...');
					}
				} else {
					console.log(
						'⚠️ Server-side action not available, using direct API call'
					);
				}

				// Try direct API call (either as primary or fallback method)
				console.log(
					`🔄 Trying direct API call to updateSceneStatus endpoint`
				);
				// Get environment variable using Vite's import.meta.env instead of process.env
				const env = import.meta.env.VITE_CONVEX_API_URL;
				console.log('🔄 Environment:', env);

				// Create a specialized HTTP request with custom headers to work around CORS issues
				const response = await fetch(
					`${env}/updateSceneStatus`,
					// `https://steady-viper-971.convex.site/updateSceneStatus`,
					{
						method: 'POST',
						headers: {
							'Content-Type': 'application/json',
							Accept: 'application/json',
							'X-Requested-With': 'XMLHttpRequest',
						},
						mode: 'cors',
						credentials: 'omit',
						body: JSON.stringify({
							sceneId: idToSend,
							status,
						}),
					}
				);

				if (!response.ok) {
					throw new Error(
						`Error ${response.status}: ${await response.text()}`
					);
				}

				const result = await response.json();
				console.log(
					`✅ Database status updated to ${status} for scene ${sceneId}`,
					result
				);
				return true;
			} catch (error) {
				console.error(
					`❌ ALL API calls failed to update status: ${error}`
				);

				// Even if API calls fail, we've already updated local state
				// So the UI will still reflect the regeneration state
				console.log(
					'⚠️ Continuing with local state only, database update failed'
				);
				return false;
			}
		},
		[videoScenes, setRegeneratingSceneIds, updateSceneStatusAction] // Removed updateSceneStatusAction since it's always undefined
	);

	// Handle rendering the video with the video engine API
	const renderVideo = async () => {
		if (
			!videoData ||
			!formattedVideoData ||
			!videoScenes ||
			videoScenes.length === 0
		) {
			toast({
				title: 'Error',
				description: 'Video data or scenes are not available yet',
				variant: 'destructive',
			});
			return;
		}

		setIsRendering(true);

		try {
			// Get the selected video scene based on the selectedSceneIndex
			const selectedScene = videoScenes[selectedSceneIndex];

			if (!selectedScene) {
				throw new Error('Selected scene not found');
			}

			// We need to use the externalId from the scene, not the _id
			if (!selectedScene.externalId) {
				throw new Error('Selected scene has no externalId');
			}

			// Use the scene's externalId as the recordId for the API call
			const recordId = selectedScene.externalId;

			// Create the payload for the API
			const payload = {
				sceneId: recordId, // Using externalId as the sceneId as required by server
				audioUrl: videoData.audioUrl,
				captionJson: videoData.captionJson || [],
				images: formattedVideoData.images || [],
				caption: {
					name: 'Glitch',
					style: {
						borderRadius: '0.5rem',
						color: '#ec4899',
						fontSize: '1.875rem',
						fontWeight: '900',
						letterSpacing: '0.05em',
						padding: '0.25rem 0.75rem',
						textShadow:
							'4px 4px 0 rgba(0, 0, 0, 0.8), -4px -4px 0 rgba(0, 150, 255, 0.8)',
						textTransform: 'uppercase',
					},
				},
				videoStyle: 'Watchers',
				videoName: videoData.title || 'Watchers',
			};

			console.log('Sending payload via Convex action:', payload);

			// Call the Convex action which will make the API call server-side
			const result = await renderVideoAction(payload);
			console.log('API response through Convex:', result);

			// Check for download URL in the response (check both downloadUrl and data.videoUrl)
			const hasDownloadUrl =
				result.downloadUrl || (result.data && result.data.videoUrl);

			if (hasDownloadUrl) {
				toast({
					title: 'Video Rendering Started',
					description:
						'Your video is ready & available for download.',
				});
			} else {
				toast({
					title: 'Video Rendering Started',
					description:
						'Your video is now being rendered. This may take a few minutes.',
				});
			}

			// Set a timeout to stop the rendering state after a reasonable time
			setTimeout(() => {
				setIsRendering(false);
			}, 15000);
		} catch (error) {
			console.error('Error rendering video:', error);
			toast({
				title: 'Rendering Failed',
				description:
					error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive',
			});
			setIsRendering(false);
		}
	};

	// Create direct UI state for buttons
	const [processingScenes, setProcessingScenes] = useState<Set<string>>(
		new Set()
	);

	// Function to regenerate a scene
	const regenerateScene = async (sceneExternalId: string | null) => {
		console.log('🔄 REGENERATE SCENE CONFIRMED:', sceneExternalId);

		if (!sceneExternalId) {
			toast({
				title: 'Error',
				description: 'Missing scene external ID',
				variant: 'destructive',
			});
			return;
		}

		if (processingScenes.has(sceneExternalId)) {
			console.warn(
				`🔄 Scene ${sceneExternalId} is already being processed. Ignoring duplicate request.`
			);
			return; // Exit early if already processing
		}

		// Immediately disable the button by tracking this externalId
		setProcessingScenes((prev) => new Set([...prev, sceneExternalId]));

		// Show immediate toast
		toast({
			title: 'Scene Regeneration Started',
			description: 'Contacting regeneration service...',
		});

		// Find the scene in Convex using the externalId
		const sceneToRegenerate = videoScenes?.find(
			(s: VideoScene) => s.externalId === sceneExternalId
		);

		if (!sceneToRegenerate || !sceneToRegenerate._id) {
			toast({
				title: 'Error',
				description: 'Could not find the scene details in Convex.',
				variant: 'destructive',
			});
			setProcessingScenes((prev) => {
				const newSet = new Set(prev);
				newSet.delete(sceneExternalId);
				return newSet;
			});
			return;
		}

		const convexId = sceneToRegenerate._id;
		console.log('🎯 Found scene with Convex ID:', convexId.toString());

		// Update local state and database - ensure status is 'regenerating'
		const statusUpdateSuccess = await updateSceneStatus(
			convexId,
			'regenerating'
		);
		console.log(
			`🔄 Status update to 'regenerating' ${statusUpdateSuccess ? 'succeeded' : 'failed'}`
		);

		// Now, call the external regeneration API directly
		try {
			console.log('📡 Calling external API...');
			const payload = {
				videoId,
				sceneId: sceneExternalId, // Send EXTERNAL ID to the API
				status: 'regenerating', // Explicitly request 'regenerating' status
			};
			console.log('📦 Payload for /api/regenerate-scene:', payload);

			const response = await fetch(
				'https://video.prometheus.social/api/regenerate-scene',
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Accept: 'application/json',
					},
					body: JSON.stringify(payload), // Use the payload object here
				}
			);

			console.log('📞 API Response status:', response.status);

			if (!response.ok) {
				// If API call fails, remove from processing set
				setProcessingScenes((prev) => {
					const newSet = new Set(prev);
					newSet.delete(sceneExternalId);
					return newSet;
				});
				throw new Error(
					`Error ${response.status}: ${await response.text()}`
				);
			}

			const result = await response.json();
			console.log(
				'✅ Scene regeneration initiated via external API:',
				result
			);

			toast({
				title: 'Scene Regeneration Initiated',
				description:
					'Your scene is being regenerated. The UI will update automatically when complete.',
			});

			// We'll keep tracking in processingScenes to keep the button disabled
			// But will clear it when image URL changes in the tracking useEffect
		} catch (error) {
			console.error(
				'🔴 Error regenerating scene via external API:',
				error
			);
			toast({
				title: 'Regeneration API Failed',
				description:
					error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive',
			});

			// Reset the processing state on error
			setProcessingScenes((prev) => {
				const newSet = new Set(prev);
				newSet.delete(sceneExternalId);
				return newSet;
			});
		}
	};

	// Function to handle opening the regeneration confirmation dialog
	const handleRegenerateClick = (sceneExternalId: string | undefined) => {
		if (!sceneExternalId) {
			toast({
				title: 'Error',
				description: 'Missing scene external ID',
				variant: 'destructive',
			});
			return;
		}
		console.log('❓ Prompting for regeneration:', sceneExternalId);
		setSceneToRegenerateId(sceneExternalId);
		setIsRegenerateConfirmOpen(true);
	};

	// Function to approve a scene
	const approveScene = async (sceneExternalId: string | undefined) => {
		console.log('👍 APPROVE SCENE CLICKED:', sceneExternalId);

		if (!sceneExternalId) {
			toast({
				title: 'Error',
				description: 'Missing scene external ID',
				variant: 'destructive',
			});
			return;
		}

		// Set the clicked scene ID to trigger animation
		setClickedApproveSceneId(sceneExternalId);

		// Reset the animation after a delay
		setTimeout(() => {
			setClickedApproveSceneId(null);
		}, 2000);

		// Find the scene in Convex using the externalId
		const sceneToApprove = videoScenes?.find(
			(s: VideoScene) => s.externalId === sceneExternalId
		);

		if (!sceneToApprove || !sceneToApprove._id) {
			toast({
				title: 'Error',
				description: 'Could not find the scene details in Convex.',
				variant: 'destructive',
			});
			return;
		}

		const convexId = sceneToApprove._id;
		console.log('🎯 Found scene with Convex ID:', convexId.toString());

		// Update the scene status to 'approved'
		try {
			const statusUpdateSuccess = await updateSceneStatus(
				convexId,
				'approved'
			);
			console.log(
				`👍 Status update to 'approved' ${statusUpdateSuccess ? 'succeeded' : 'failed'}`
			);

			if (statusUpdateSuccess) {
				toast({
					title: 'Scene Approved',
					description: 'The scene has been successfully approved.',
				});
			} else {
				throw new Error('Failed to update scene status');
			}
		} catch (error) {
			console.error('🔴 Error approving scene:', error);
			toast({
				title: 'Approval Failed',
				description:
					error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive',
			});
		}
	};

	// Function to disapprove (revert from approved to completed) a scene
	const disapproveScene = async (sceneExternalId: string | undefined) => {
		console.log('👎 DISAPPROVE SCENE CLICKED:', sceneExternalId);

		if (!sceneExternalId) {
			toast({
				title: 'Error',
				description: 'Missing scene external ID',
				variant: 'destructive',
			});
			return;
		}

		// Set the clicked scene ID to trigger animation
		setClickedDisapproveSceneId(sceneExternalId);

		// Reset the animation after a delay
		setTimeout(() => {
			setClickedDisapproveSceneId(null);
		}, 2000);

		// Find the scene in Convex using the externalId
		const sceneToDisapprove = videoScenes?.find(
			(s: VideoScene) => s.externalId === sceneExternalId
		);

		if (!sceneToDisapprove || !sceneToDisapprove._id) {
			toast({
				title: 'Error',
				description: 'Could not find the scene details in Convex.',
				variant: 'destructive',
			});
			return;
		}

		const convexId = sceneToDisapprove._id;
		console.log('🎯 Found scene with Convex ID:', convexId.toString());

		// Update the scene status from approved to completed
		try {
			const statusUpdateSuccess = await updateSceneStatus(
				convexId,
				'completed'
			);
			console.log(
				`👎 Status update to 'completed' ${statusUpdateSuccess ? 'succeeded' : 'failed'}`
			);

			if (statusUpdateSuccess) {
				toast({
					title: 'Scene Not Approved',
					description: 'The scene is no longer approved.',
				});
			} else {
				throw new Error('Failed to update scene status');
			}
		} catch (error) {
			console.error('🔴 Error disapproving scene:', error);
			toast({
				title: 'Disapproval Failed',
				description:
					error instanceof Error ? error.message : 'Unknown error',
				variant: 'destructive',
			});
		}
	};

	// Track image URL changes to automatically mark scenes as completed
	const [previousScenes, setPreviousScenes] = useState<VideoScene[]>([]);

	// Function to preload images for a scene
	const preloadImagesForScene = (sceneId: string, imageUrls: string[]) => {
		if (preloadedImages[sceneId] || imageUrls.length === 0) return;

		// console.log(
		// 	`Preloading ${imageUrls.length} images for scene ${sceneId}`
		// );

		// Create an array to track loading status of all images
		const imagePromises = imageUrls.map((url) => {
			return new Promise<void>((resolve) => {
				const img = new Image();
				img.onload = () => resolve();
				img.onerror = () => resolve(); // Resolve even on error to continue
				img.src = url;
			});
		});

		// Mark as preloaded once all images are loaded
		Promise.all(imagePromises).then(() => {
			// console.log(`Finished preloading images for scene ${sceneId}`);
			setPreloadedImages((prev) => ({
				...prev,
				[sceneId]: true,
			}));
		});
	};

	useEffect(() => {
		if (!videoScenes || videoScenes.length === 0) {
			setPreviousScenes([]);
			return;
		}

		// Add keyframe animations to the document head if they don't exist
		if (!document.getElementById('carousel-animations')) {
			const styleEl = document.createElement('style');
			styleEl.id = 'carousel-animations';
			styleEl.innerHTML = `
				@keyframes fadeIn {
					from { opacity: 0; }
					to { opacity: 1; }
				}
				@keyframes fadeOut {
					from { opacity: 1; }
					to { opacity: 0; }
				}
			`;
			document.head.appendChild(styleEl);
		}

		// Preload images for all scenes when videoScenes data is available
		videoScenes.forEach((scene: VideoScene) => {
			if (!scene._id) return;

			const sceneId = scene._id.toString();

			try {
				// Extract image URLs for preloading
				const imageData = JSON.parse(scene.imageUrl);
				const imagesToPreload: string[] = [];

				// Add all image variations with positions 1-4
				if (
					imageData?.variations &&
					Array.isArray(imageData.variations)
				) {
					imageData.variations
						.filter((v) => v.position >= 1 && v.position <= 4)
						.sort((a, b) => a.position - b.position)
						.forEach((v) => {
							if (v.url) imagesToPreload.push(v.url);
						});
				}

				// Preload the images
				if (imagesToPreload.length > 0) {
					preloadImagesForScene(sceneId, imagesToPreload);
				}
			} catch {
				// If parsing fails, try to preload the raw URL
				if (typeof scene.imageUrl === 'string') {
					preloadImagesForScene(sceneId, [scene.imageUrl]);
				}
			}
		});

		// Check for imageURL changes on regenerating scenes
		for (const scene of videoScenes) {
			if (!scene.externalId) continue;

			// Skip if not being processed
			if (!processingScenes.has(scene.externalId)) continue;

			const previousScene = previousScenes.find(
				(s: VideoScene) => s.externalId === scene.externalId
			);

			if (previousScene && scene.imageUrl !== previousScene.imageUrl) {
				console.log(
					`🖼️ Detected image change for scene ${scene.externalId}!`
				);
				console.log(
					'Previous image:',
					previousScene.imageUrl.substring(0, 50) + '...'
				);
				console.log(
					'New image:',
					scene.imageUrl.substring(0, 50) + '...'
				);

				// Image URL changed, mark as completed locally
				console.log(
					'🎉 Marking scene as completed due to image change'
				);

				// Update the scene's status locally
				if (scene._id) {
					updateSceneStatus(scene._id, 'completed');
				}

				// Remove from processing to re-enable the button
				setProcessingScenes((prev) => {
					const newSet = new Set(prev);
					newSet.delete(scene.externalId!);
					return newSet;
				});

				// Show completion toast
				toast({
					title: 'Scene Regenerated',
					description: 'The scene has been successfully regenerated.',
				});
			}
		}

		// Store current scenes for the next comparison
		setPreviousScenes(videoScenes);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [videoScenes, processingScenes, updateSceneStatus]);

	// Effect to track scene updates and manage regeneration status
	useEffect(() => {
		if (!videoScenes || regeneratingSceneIds.length === 0) {
			return;
		}

		console.log(
			'🔍 Checking regeneration status for scenes:',
			regeneratingSceneIds
		);
		const stillRegenerating: string[] = [];
		const newlyCompleted: string[] = [];

		regeneratingSceneIds.forEach((scene_id_string) => {
			const scene = videoScenes.find(
				(s: VideoScene) => s._id?.toString() === scene_id_string
			);

			// Check if the scene has been updated (status change or imageUrl change)
			if (scene) {
				// IMPORTANT: Only consider it completed if the status is explicitly NOT 'regenerating'
				// AND one of these conditions is true:
				// 1. Status is explicitly 'completed'
				// 2. We detect a change in the image URL from before
				const previousScene = previousScenes.find(
					(s: VideoScene) => s._id?.toString() === scene_id_string
				);

				const imageChanged =
					previousScene && scene.imageUrl !== previousScene.imageUrl;
				const isExplicitlyCompleted = scene.status === 'completed';
				const isNotRegenerating = scene.status !== 'regenerating';

				// Add detailed logging
				console.log(`Scene ${scene_id_string} status check:`, {
					currentStatus: scene.status,
					imageChanged: imageChanged,
					explicitlyCompleted: isExplicitlyCompleted,
					notRegenerating: isNotRegenerating,
				});

				// Only mark complete if status changed from regenerating OR image changed
				if (
					isNotRegenerating &&
					(isExplicitlyCompleted || imageChanged)
				) {
					console.log(
						`✅ Scene ${scene_id_string} finished regenerating (status: ${scene.status}, imageChanged: ${imageChanged}).`
					);
					newlyCompleted.push(scene_id_string);

					// If a scene completed but status is missing, update it to completed locally
					if (!scene.status) {
						console.log(
							"🔄 Status missing, marking as 'completed' locally"
						);
						// We'll just update the local state since we can't update the DB easily
						const updateCompletionStatus = async () => {
							try {
								await updateSceneStatus(scene._id, 'completed');
								console.log(
									'✅ Successfully updated local completion status'
								);
							} catch (error) {
								console.error(
									'❌ Failed to update completion status:',
									error
								);
							}
						};
						// Call the async function
						updateCompletionStatus();
					}
				} else {
					// Still regenerating
					console.log(
						`⏳ Scene ${scene_id_string} still regenerating (status: ${scene?.status || 'unknown'}).`
					);
					stillRegenerating.push(scene_id_string);
				}
			} else {
				// Scene might have been deleted or data is inconsistent, remove from tracking
				console.warn(
					`⚠️ Scene ${scene_id_string} tracked for regeneration not found in current data.`
				);
			}
		});

		if (newlyCompleted.length > 0) {
			console.log('✅ Updating regenerating scene IDs:', {
				before: regeneratingSceneIds,
				after: stillRegenerating,
				completed: newlyCompleted,
			});
			// Update the local tracking state
			setRegeneratingSceneIds(stillRegenerating);
		}
	}, [videoScenes, regeneratingSceneIds, updateSceneStatus, previousScenes]);

	// Add a new function to check if all scenes are approved
	const areAllScenesApproved = useMemo(() => {
		if (!videoScenes || videoScenes.length === 0) return false;
		return videoScenes.every(
			(scene: VideoScene) => scene.status === 'approved'
		);
	}, [videoScenes]);

	// Navigation logic
	if (!videoId) {
		return (
			<div className='video-container py-8 px-8 w-full'>
				<div className='flex flex-col items-center gap-4'>
					<h1 className='text-2xl font-bold mb-4'>
						No Video Selected
					</h1>
					<Button onClick={() => navigate('/admin/video')}>
						Return to Videos
					</Button>
				</div>
			</div>
		);
	}

	// While data is loading
	if (videoData === undefined) {
		return (
			<div className='video-container py-8 px-8 w-full'>
				<Loader className='py-12' message='LOADING VIDEO DATA...' />
			</div>
		);
	}

	// If video not found
	if (videoData === null) {
		return (
			<div className='video-container py-8 px-8 w-full'>
				<div className='flex flex-col items-center gap-4'>
					<h1 className='text-2xl font-bold mb-4'>Video not found</h1>
					<Button onClick={() => navigate('/admin/video')}>
						Return to Videos
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className='video-container py-8 px-8 w-full'>
			<div className='flex items-center justify-between mb-6'>
				<div className='flex items-center gap-4'>
					<Button
						variant='outline'
						size='lg'
						onClick={() => {
							navigate('/admin/video', {
								replace: true,
							});
						}}
						className='hover:bg-secondary text-2xl font-bold px-8 py-6 mb-4'
					>
						← BACK TO ALL VIDEOS
					</Button>
				</div>
				<div className='flex items-center gap-2'>
					{areAllScenesApproved && (
						<Button
							onClick={renderVideo}
							disabled={isRendering || !formattedVideoData}
						>
							{isRendering ? (
								<>
									<Loader className='h-4 w-4 mr-2' />
									Rendering...
								</>
							) : (
								<>
									<Play className='h-4 w-4 mr-2' />
									PUBLISH
								</>
							)}
						</Button>
					)}
				</div>
			</div>

			<div className='grid grid-cols-1 lg:grid-cols-4 gap-8 mb-40'>
				<div className='lg:col-span-4'>
					<Card className='h-full flex flex-col'>
						<CardHeader>
							<CardTitle className='pink-title text-3xl font-bold'>
								VIDEO SCENES:
							</CardTitle>
							<span className='text-muted-foreground font-normal text-white text-xl italic'>
								{videoData.title}
							</span>
							<span className='absolute top-4 left-96 flex flex-row gap-28 ml-auto mr-8 rounded-lg bg-black/30 p-4 px-14 border-2 border-gray-900'>
								<div>
									<div className='font-semibold'>
										Platform
									</div>
									<div className='text-lg'>
										{videoData.platform}
									</div>
								</div>

								<div>
									<div className='font-semibold'>
										Template
									</div>
									<div className='text-lg'>
										{videoData.template || 'watchers'}
									</div>
								</div>

								<div>
									<div className='font-semibold'>Status</div>
									<div className='text-lg'>
										{videoData.status}
									</div>
								</div>

								{videoData.script && (
									<div>
										<div className='font-semibold'>
											Story
										</div>
										<div className='text-lg max-h-80 overflow-y-auto line-clamp-1'>
											{videoData.script}
										</div>
									</div>
								)}
							</span>
						</CardHeader>

						<CardContent className='flex-grow p-4'>
							{!videoScenes ? (
								<Loader
									className='py-12'
									message='LOADING VIDEO SCENES...'
								/>
							) : videoScenes.length === 0 ? (
								<div className='flex justify-center items-center h-full'>
									<p className='text-muted-foreground'>
										No scenes found for this video
									</p>
								</div>
							) : (
								<div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-20'>
									{videoScenes.map(
										(scene: VideoScene, index: number) => {
											const convexId = scene._id
												? scene._id.toString()
												: null;
											const externalId = scene.externalId;

											// Logic to extract all image URLs for the scene
											let sceneImageUrls: string[] = [];
											try {
												const imageData = JSON.parse(
													scene.imageUrl
												);

												// Only get variation URLs with positions 1-4
												if (
													imageData?.variations &&
													Array.isArray(
														imageData.variations
													)
												) {
													// Filter variations to only include positions 1-4 and sort them by position
													const filteredVariations =
														imageData.variations
															.filter(
																(v) =>
																	v.position >=
																		1 &&
																	v.position <=
																		4
															)
															.sort(
																(
																	a: unknown,
																	b: unknown
																) =>
																	(
																		a as {
																			position: number;
																		}
																	).position -
																	(
																		b as {
																			position: number;
																		}
																	).position
															);

													// Extract URLs in position order
													sceneImageUrls =
														filteredVariations
															.map((v) => v.url)
															.filter(Boolean);

													// console.log(
													// 	`Found ${sceneImageUrls.length} image variations (positions 1-4) for scene ${scene.sceneNumber}`
													// );
												}

												// If no valid variations found, fallback to mainUrl
												if (
													sceneImageUrls.length ===
														0 &&
													imageData?.mainUrl
												) {
													sceneImageUrls = [
														imageData.mainUrl,
													];
													// console.log(
													// 	`Using mainUrl fallback for scene ${scene.sceneNumber}`
													// );
												}

												// Last resort: use the raw string
												if (
													sceneImageUrls.length ===
														0 &&
													typeof scene.imageUrl ===
														'string'
												) {
													sceneImageUrls = [
														scene.imageUrl,
													];
													// console.log(
													// 	`Using raw string fallback for scene ${scene.sceneNumber}`
													// );
												}
											} catch {
												// If parsing fails, assume imageUrl is a direct URL string
												if (
													typeof scene.imageUrl ===
													'string'
												) {
													sceneImageUrls = [
														scene.imageUrl,
													];
													// console.log(
													// 	`JSON parse failed, using raw string for scene ${scene.sceneNumber}`
													// );
												}
											}

											// Extract scene data and prepare for display
											const sceneIdentifier =
												convexId || `scene-${index}`;
											const currentImageIndex =
												selectedImageIndices[
													sceneIdentifier
												] || 0;
											const hasValidUrls =
												sceneImageUrls.length > 0;

											// Calculate the safe index with wraparound protection
											const safeIndex = hasValidUrls
												? ((currentImageIndex %
														sceneImageUrls.length) +
														sceneImageUrls.length) %
													sceneImageUrls.length
												: 0;

											// Get current image URL and prepare for player
											const currentImageUrl = hasValidUrls
												? sceneImageUrls[safeIndex]
												: null;
											const playerImages = currentImageUrl
												? [currentImageUrl]
												: [];

											// Optional logging in development mode only
											if (
												currentImageUrl &&
												process.env.NODE_ENV !==
													'production'
											) {
												// console.log(
												// 	`Scene ${scene.sceneNumber}: Image ${safeIndex + 1}/${sceneImageUrls.length}`
												// );
											}

											// Modified handlers for navigation with fade effect
											const handlePrevImage = () => {
												// Prevent action if already animating
												if (
													isAnimating[sceneIdentifier]
												)
													return;

												// Calculate the next index safely
												const nextIndex =
													(((currentImageIndex - 1) %
														sceneImageUrls.length) +
														sceneImageUrls.length) %
													sceneImageUrls.length;

												// Set animation direction (still useful for tracking)
												setSlideDirection({
													...slideDirection,
													[sceneIdentifier]: 'right',
												});

												// Set animating flag
												setIsAnimating({
													...isAnimating,
													[sceneIdentifier]: true,
												});

												// Start fade out
												setFadeState({
													...fadeState,
													[sceneIdentifier]: 'out',
												});

												// Wait for fade out, then change the image
												setTimeout(() => {
													// Update the index
													setSelectedImageIndices(
														(prev) => ({
															...prev,
															[sceneIdentifier]:
																nextIndex,
														})
													);

													// Start fade in for the new image
													setFadeState({
														...fadeState,
														[sceneIdentifier]: 'in',
													});

													// Reset animation state after transition completes
													setTimeout(() => {
														setIsAnimating({
															...isAnimating,
															[sceneIdentifier]:
																false,
														});

														setFadeState({
															...fadeState,
															[sceneIdentifier]:
																'none',
														});
													}, 300); // Match transition duration
												}, 300); // Match transition duration
											};

											const handleNextImage = () => {
												// Prevent action if already animating
												if (
													isAnimating[sceneIdentifier]
												)
													return;

												// Calculate the next index safely
												const nextIndex =
													(currentImageIndex + 1) %
													sceneImageUrls.length;

												// Set animation direction (still useful for tracking)
												setSlideDirection({
													...slideDirection,
													[sceneIdentifier]: 'left',
												});

												// Set animating flag
												setIsAnimating({
													...isAnimating,
													[sceneIdentifier]: true,
												});

												// Start fade out
												setFadeState({
													...fadeState,
													[sceneIdentifier]: 'out',
												});

												// Wait for fade out, then change the image
												setTimeout(() => {
													// Update the index
													setSelectedImageIndices(
														(prev) => ({
															...prev,
															[sceneIdentifier]:
																nextIndex,
														})
													);

													// Start fade in for the new image
													setFadeState({
														...fadeState,
														[sceneIdentifier]: 'in',
													});

													// Reset animation state after transition completes
													setTimeout(() => {
														setIsAnimating({
															...isAnimating,
															[sceneIdentifier]:
																false,
														});

														setFadeState({
															...fadeState,
															[sceneIdentifier]:
																'none',
														});
													}, 300); // Match transition duration
												}, 300); // Match transition duration
											};

											// Determine if the scene is currently regenerating based on:
											// 1. DB status
											// 2. Local tracking state
											// 3. Processing scenes set (most immediate)
											const isSceneRegenerating =
												scene.status ===
													'regenerating' ||
												regeneratingSceneIds.includes(
													scene._id?.toString() || ''
												) ||
												(scene.externalId &&
													processingScenes.has(
														scene.externalId
													));

											// Debug state
											if (
												scene.externalId &&
												processingScenes.has(
													scene.externalId
												)
											) {
												console.log(
													`🔄 Scene ${scene.externalId} is in processing set, marked as regenerating`
												);
											}

											// console.log(
											// 	`Scene ${scene._id?.toString()} regenerating state:`,
											// 	{
											// 		dbStatus: scene.status,
											// 		isInLocalState:
											// 			regeneratingSceneIds.includes(
											// 				scene._id?.toString() ||
											// 					''
											// 			),
											// 		finalState:
											// 			isSceneRegenerating,
											// 	}
											// );

											return (
												<Card
													key={convexId ?? index}
													className=''
												>
													<CardContent className='p-3'>
														<div className='text-xl font-normal mb-4 text-orange-500'>
															Scene{' '}
															{scene.sceneNumber}:
														</div>

														{/* Show loader overlay if the scene status is 'regenerating' */}
														{isSceneRegenerating ? (
															<>
																<div className='absolute top-14 left-3 z-50 w-[298px] h-[528px]'>
																	<div className='bg-black/80 w-full h-full flex items-center justify-center rounded-lg border-4 border-pink-500'>
																		<Loader className='h-4 w-4 mt-[-120px] ' />
																	</div>
																</div>
															</>
														) : (
															<></>
														)}

														<div className='mb-3 relative overflow-hidden'>
															{/* Left arrow */}
															{sceneImageUrls.length >
																1 && (
																<div
																	className='absolute left-11 top-1/2 transform -translate-y-1/2 z-10 hover:cursor-pointer bg-black/70 rounded-full'
																	onClick={
																		handlePrevImage
																	}
																	style={{
																		filter: 'drop-shadow(0 0 12px rgba(236, 72, 153, 0.9)) drop-shadow(0 0 20px rgba(236, 72, 153, 0.6)) drop-shadow(0 0 30px rgba(236, 72, 153, 0.4))',
																		transform:
																			'scale(1.5) translateY(98px)',
																		marginLeft:
																			'10px',
																	}}
																	onMouseEnter={() =>
																		setHoveredArrows(
																			{
																				...hoveredArrows,
																				[sceneIdentifier]:
																					'left',
																			}
																		)
																	}
																	onMouseLeave={() =>
																		setHoveredArrows(
																			{
																				...hoveredArrows,
																				[sceneIdentifier]:
																					null,
																			}
																		)
																	}
																>
																	<LeftArrowIcon
																		isHovered={
																			hoveredArrows[
																				sceneIdentifier
																			] ===
																			'left'
																		}
																	/>
																</div>
															)}

															<div className='relative w-full'>
																{formattedVideoData ? (
																	<div
																		style={{
																			opacity:
																				fadeState[
																					sceneIdentifier
																				] ===
																				'out'
																					? 0
																					: 1,
																			transition:
																				'opacity 0.33s ease-in-out',
																			willChange:
																				'opacity',
																		}}
																	>
																		<RemotionPlayer
																			key={`${sceneIdentifier}-${currentImageIndex}`}
																			videoData={{
																				...formattedVideoData,
																				images: playerImages,
																			}}
																			size='normal'
																		/>
																	</div>
																) : (
																	<div className='flex justify-center items-center h-full bg-muted'>
																		<p className='text-xs text-muted-foreground'>
																			Loading
																			scene
																			data...
																		</p>
																	</div>
																)}
															</div>

															{/* Right arrow */}
															{sceneImageUrls.length >
																1 && (
																<div
																	className='absolute right-11 top-1/2 transform -translate-y-1/2 z-10 hover:cursor-pointer bg-black/70 rounded-full'
																	onClick={
																		handleNextImage
																	}
																	style={{
																		filter: 'drop-shadow(0 0 12px rgba(236, 72, 153, 0.9)) drop-shadow(0 0 20px rgba(236, 72, 153, 0.6)) drop-shadow(0 0 30px rgba(236, 72, 153, 0.4))',
																		transform:
																			'scale(1.5) translateY(98px)',
																		marginLeft:
																			'10px',
																	}}
																	onMouseEnter={() =>
																		setHoveredArrows(
																			{
																				...hoveredArrows,
																				[sceneIdentifier]:
																					'right',
																			}
																		)
																	}
																	onMouseLeave={() =>
																		setHoveredArrows(
																			{
																				...hoveredArrows,
																				[sceneIdentifier]:
																					null,
																			}
																		)
																	}
																>
																	<RightArrowIcon
																		isHovered={
																			hoveredArrows[
																				sceneIdentifier
																			] ===
																			'right'
																		}
																	/>
																</div>
															)}

															{/* Image counter indicator */}
															{sceneImageUrls.length >
																1 && (
																<div className='absolute bottom-20 left-0 right-0 flex justify-center'>
																	<div
																		className='bg-black/70 text-white font-semibold text-lg px-4 py-1 rounded-full'
																		style={{
																			filter: 'drop-shadow(0 0 12px rgba(236, 72, 153, 0.9)) drop-shadow(0 0 20px rgba(236, 72, 153, 0.6)) drop-shadow(0 0 30px rgba(236, 72, 153, 0.4))',
																		}}
																	>
																		{currentImageIndex +
																			1}{' '}
																		/{' '}
																		{
																			sceneImageUrls.length
																		}
																	</div>
																</div>
															)}
														</div>
														<div className='flex gap-2 mt-3 w-full'>
															<TooltipProvider
																delayDuration={
																	0
																}
															>
																<Tooltip
																	delayDuration={
																		0
																	}
																>
																	<TooltipTrigger
																		asChild
																	>
																		<Button
																			variant='secondary'
																			size='lg'
																			className={`flex-1 py-8 px-2 text-white ${
																				scene.status ===
																				'approved'
																					? 'bg-gradient-to-b from-pink-500 via-pink-600 to-purple-700'
																					: 'bg-gradient-to-b from-pink-500 via-pink-600 to-purple-700 hover:from-pink-600 hover:to-purple-800'
																			} transition-all duration-200 hover:scale-[1.1] rounded-xl`}
																			onClick={() =>
																				approveScene(
																					externalId
																				)
																			}
																			disabled={
																				!externalId ||
																				isSceneRegenerating ||
																				Boolean(
																					externalId &&
																						processingScenes.has(
																							externalId
																						)
																				) ||
																				scene.status ===
																					'approved'
																			}
																		>
																			<div className='flex items-center justify-center gap-2'>
																				<SmileIcon
																					isHovered={
																						externalId ===
																						clickedApproveSceneId
																					}
																				/>
																				{/* <div className='text-lg text-white font-bold'>
																					{scene.status === 'approved' ? '✓' : ''}
																				</div> */}
																			</div>
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent
																		side='top'
																		className='bg-pink-900 text-white border-pink-700 z-[9999]'
																	>
																		<p>
																			Approve
																			Scene
																		</p>
																	</TooltipContent>
																</Tooltip>
															</TooltipProvider>

															<TooltipProvider
																delayDuration={
																	0
																}
															>
																<Tooltip
																	delayDuration={
																		0
																	}
																>
																	<TooltipTrigger
																		asChild
																	>
																		<Button
																			variant='secondary'
																			size='lg'
																			className={`flex-1 py-8 px-2 text-white ${
																				scene.status ===
																				'approved'
																					? 'bg-gradient-to-b from-red-500 via-red-600 to-red-800 hover:from-red-600 hover:to-red-900'
																					: 'bg-gradient-to-b from-red-400 via-red-400 to-red-500 opacity-50'
																			} transition-all duration-200 hover:scale-[1.1] rounded-xl`}
																			onClick={() =>
																				disapproveScene(
																					externalId
																				)
																			}
																			disabled={
																				!externalId ||
																				isSceneRegenerating ||
																				Boolean(
																					externalId &&
																						processingScenes.has(
																							externalId
																						)
																				) ||
																				scene.status !==
																					'approved'
																			}
																		>
																			<div className='flex items-center justify-center'>
																				<FrownIcon
																					isHovered={
																						externalId ===
																						clickedDisapproveSceneId
																					}
																				/>
																			</div>
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent
																		side='top'
																		className='bg-red-900 text-white border-red-700 z-[9999]'
																	>
																		<p>
																			Disapprove
																			Scene
																		</p>
																	</TooltipContent>
																</Tooltip>
															</TooltipProvider>

															<TooltipProvider
																delayDuration={
																	0
																}
															>
																<Tooltip
																	delayDuration={
																		0
																	}
																>
																	<TooltipTrigger
																		asChild
																	>
																		<Button
																			variant='secondary'
																			size='lg'
																			className={`flex-1 py-8 px-2 text-white bg-gradient-to-b from-orange-400 via-orange-500 to-orange-700 hover:from-orange-500 hover:to-orange-800 transition-all duration-200 hover:scale-[1.1] rounded-xl`}
																			onClick={() =>
																				handleRegenerateClick(
																					externalId
																				)
																			}
																			disabled={
																				!externalId ||
																				isSceneRegenerating ||
																				Boolean(
																					externalId &&
																						processingScenes.has(
																							externalId
																						)
																				)
																			}
																		>
																			{isSceneRegenerating ? (
																				<>
																					<RefreshCw className='animate-spin h-8 w-8' />
																				</>
																			) : (
																				<>
																					<RefreshCw className='h-8 w-8' />
																				</>
																			)}
																		</Button>
																	</TooltipTrigger>
																	<TooltipContent
																		side='top'
																		className='bg-orange-900 text-white border-orange-700 z-[9999]'
																	>
																		<p>
																			Regenerate
																			Scene
																		</p>
																	</TooltipContent>
																</Tooltip>
															</TooltipProvider>
														</div>
														<div className='mt-11 mb-6 text-center font-medium'>
															<span
																className={`px-6 py-3 rounded-full text-xl ${
																	isSceneRegenerating ||
																	processingScenes.has(
																		externalId ||
																			''
																	)
																		? 'bg-amber-500/20 text-amber-300'
																		: scene.status ===
																			  'approved'
																			? 'bg-green-500/20 text-green-300'
																			: 'bg-gray-500/20 text-gray-300'
																}`}
															>
																{isSceneRegenerating ||
																processingScenes.has(
																	externalId ||
																		''
																)
																	? 'REGENERATING...'
																	: scene.status ===
																		  'approved'
																		? 'APPROVED'
																		: scene.status ===
																			  'completed'
																			? 'DRAFT'
																			: scene.status ||
																				'DRAFT'}
															</span>
														</div>
													</CardContent>
												</Card>
											);
										}
									)}
								</div>
							)}
						</CardContent>
					</Card>
				</div>
			</div>

			<ConfirmDialog
				isOpen={isRegenerateConfirmOpen}
				onClose={() => setIsRegenerateConfirmOpen(false)}
				title='Confirm Scene Regeneration'
				className='shadow-[0_0_40px_10px_rgba(236,72,153,0.7)] rounded-2xl'
				description={
					<div>
						{/* <div className='flex items-center justify-center gap-2 my-8'>
							<a
								href='https://etherscan.io/token/0xCfFEEC353C74fB6097A9265E32a15d18b84193C5'
								target='_blank'
								rel='noopener noreferrer'
								className='text-lg font-bold text-pink-500 hover:text-pink-400 transition-colors'
							>
								BUY $KARMA
							</a>
						</div> */}
						<div className='flex items-center justify-center gap-2 my-8'>
							<span className='text-2xl font-bold text-gray-500'>
								$KARMA BALANCE:
							</span>
							<div>
								{user && (
									<CreditBalance
										userId={
											user.id as unknown as Id<'users'>
										}
									/>
								)}
							</div>
						</div>
						{/* Combine Scene Regeneration text and cost onto one line */}
						<div className='flex items-center justify-center font-semibold text-2xl text-white p-3 my-8'>
							<span>Scene Regeneration:</span>
							<span className='ml-2 text-3xl text-orange-500'>
								<strong className='text-pink-600'>
									3 $KARMA
								</strong>
							</span>
						</div>
					</div>
				}
				onConfirm={() => {
					regenerateScene(sceneToRegenerateId);
					setIsRegenerateConfirmOpen(false); // Close dialog after confirmation
				}}
				confirmText='CONFIRM TRANSACTION'
				cancelText='CANCEL'
				// isWarningDialog // Optional: Style as a warning
			/>
		</div>
	);
};
